import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert
} from '@mui/material';
import { useOrganization } from '../../../contexts/OrganizationContext';

const RenameOrganizationDialog = ({ open, onClose, organization }) => {
  const { renameOrganization, loading } = useOrganization();

  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (organization) {
      setName(organization.name);
    }
  }, [organization]);

  const handleClose = () => {
    setName('');
    setError(null);
    setValidationError('');
    onClose();
  };

  const validateName = (value) => {
    if (!value.trim()) {
      setValidationError('Organization name is required');
      return false;
    }
    if (value.trim().length < 2) {
      setValidationError('Organization name must be at least 2 characters');
      return false;
    }
    if (value.trim().length > 100) {
      setValidationError('Organization name must be less than 100 characters');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    validateName(value);
  };

  const handleSubmit = async () => {
    if (!validateName(name)) {
      return;
    }

    if (name.trim() === organization.name) {
      handleClose();
      return;
    }

    setError(null);

    try {
      await renameOrganization(organization.id, name.trim());
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to rename organization');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !validationError && name.trim()) {
      handleSubmit();
    }
  };

  if (!organization) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename Organization</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Organization Name"
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={handleNameChange}
          onKeyPress={handleKeyPress}
          error={!!validationError}
          helperText={validationError}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !!validationError || !name.trim() || name.trim() === organization.name}
        >
          {loading ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameOrganizationDialog;
